import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2 } from 'lucide-react';
import { useTenant } from '../context/TenantContext';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  const { branding } = useTenant();
  const tenantName = branding?.business_name || 'our academy';

  useEffect(() => {
    const pollStatus = async (attempts = 0) => {
      if (!sessionId || attempts >= 5) {
        setStatus(attempts >= 5 ? 'timeout' : 'error');
        return;
      }

      try {
        const response = await api.get(`/payments/status/${sessionId}`);
        if (response.data.payment_status === 'paid') {
          setStatus('success');
        } else if (response.data.status === 'expired') {
          setStatus('expired');
        } else {
          setTimeout(() => pollStatus(attempts + 1), 2000);
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        setTimeout(() => pollStatus(attempts + 1), 2000);
      }
    };

    pollStatus();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardContent className="pt-8 pb-8 text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="h-16 w-16 text-blue-500 mx-auto animate-spin mb-4" />
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-2">
                Processing Payment...
              </h2>
              <p className="text-slate-600">Please wait while we confirm your payment.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="h-16 w-16 text-blue-500 mx-auto mb-4" />
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-2">
                Payment Successful!
              </h2>
              <p className="text-slate-600 mb-6">
                Your enrollment has been confirmed. Welcome to {tenantName}!
              </p>
              <Button
                onClick={() => navigate('/parent')}
                className="bg-blue-500 hover:bg-blue-600"
                data-testid="go-to-dashboard-btn"
              >
                Go to Dashboard
              </Button>
            </>
          )}

          {(status === 'error' || status === 'timeout' || status === 'expired') && (
            <>
              <div className="h-16 w-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-amber-600 text-2xl">!</span>
              </div>
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-2">
                Payment Status Unknown
              </h2>
              <p className="text-slate-600 mb-6">
                We couldn't confirm your payment status. Please check your email or contact support.
              </p>
              <Button
                onClick={() => navigate('/parent')}
                variant="outline"
                data-testid="go-to-dashboard-btn"
              >
                Go to Dashboard
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
